import React, { useState, useRef } from 'react';
import { aiAPI } from '../api/client';
import { toast } from 'react-toastify';
import '../pages/AnimationsAndStyles.css';

/* ================= STYLES - DEFINED BEFORE COMPONENT ================= */

const styles = {
  uploadContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '16px',
  },
  fileManagerBtn: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    width: '120px',
    height: '120px',
    borderRadius: '16px',
    border: '2px dashed #1E40AF',
    background: 'linear-gradient(135deg, #EFF6FF 0%, #F0F4FF 100%)',
    cursor: 'pointer',
    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
    padding: '0',
    fontSize: '14px',
    fontWeight: '600',
    color: '#1E40AF',
    boxShadow: '0 4px 12px rgba(30, 64, 175, 0.1)',
  },
  plusIcon: {
    fontSize: '48px',
    fontWeight: '800',
    color: '#1E40AF',
    marginBottom: '4px',
    display: 'block',
    animation: 'float 3s ease-in-out infinite',
  },
  uploadText: {
    fontSize: '12px',
    fontWeight: '600',
    color: '#1E40AF',
  },
  uploadHint: {
    fontSize: '12px',
    color: '#94A3B8',
    margin: '0',
    textAlign: 'center',
  },
};

export default function AIAssistant() {
  const [resumeText, setResumeText] = useState('');
  const [resumeFileName, setResumeFileName] = useState('');
  const [jobDescription, setJobDescription] = useState('');
  const [analysis, setAnalysis] = useState(null);
  const [matchPercent, setMatchPercent] = useState(null);
  const [suggestions, setSuggestions] = useState(null);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('analyze');
  const fileInputRef = useRef(null);

  /* ================= FILE MANAGER TRIGGER ================= */

  // eslint-disable-next-line no-unused-vars
  const openFileManager = () => {
    fileInputRef.current?.click();
  };

  /* ================= COPY ================= */

  const copyToClipboard = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success('Copied to clipboard');
    } catch {
      toast.error('Copy failed');
    }
  };

  /* ================= ANALYZE RESUME ================= */

  const handleAnalyzeResume = async (e) => {
    e.preventDefault();

    if (!resumeText) return toast.error('Upload resume first');
    if (!jobDescription) return toast.error('Enter job description');

    setLoading(true);

    try {
      const res = await aiAPI.analyzeResume({
        resumeText,
        jobDescription
      });

      const parsed = res.data.analysis || {};

      /* ===================================================
         ✅ SAFE SCORE HANDLING (FIXES 2200% ISSUE)
         - Supports backend returning:
           0–1  OR  0–100
         - Never exceeds 100%
      =================================================== */

      const rawScore =
        parsed.matchScore ??
        parsed.match_score ??
        parsed.score ??
        0;

      const percent =
        rawScore <= 1
          ? Math.round(rawScore * 100) // 0–1 format
          : Math.round(rawScore);      // already %

      const safePercent = Math.min(100, Math.max(0, percent));

      setAnalysis(parsed);
      setMatchPercent(safePercent);

    } catch (err) {
      console.error(err);
      toast.error('AI analysis failed');
      setAnalysis(null);
      setMatchPercent(null);
    } finally {
      setLoading(false);
    }
  };

  /* ================= JD SUGGESTIONS ================= */

  const handleGetSuggestions = async (e) => {
    e.preventDefault();
    if (!jobDescription) return toast.error('Enter job description');

    setLoading(true);
    try {
      const res = await aiAPI.getJDSuggestions({ jobDescription });
      setSuggestions(res.data.suggestions);
    } catch {
      toast.error('Failed to generate JD suggestions');
    } finally {
      setLoading(false);
    }
  };

  /* ================= FILE UPLOAD ================= */

  const handleResumeFile = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setResumeFileName(file.name);

    const reader = new FileReader();
    reader.onload = (e) => setResumeText(e.target.result);
    reader.onerror = () => toast.error('Failed to read file');
    reader.readAsText(file);
  };

  return (
    <div style={page}>
      <style>{css}</style>

      <div className="card">
        <h2>AI Assistant</h2>
        <p className="subtitle">
          AI-powered resume matching & job intelligence
        </p>

        {/* ================= TABS ================= */}
        <div className="tabs">
          <button
            className={activeTab === 'analyze' ? 'tab active' : 'tab'}
            onClick={() => setActiveTab('analyze')}
          >
            Resume vs JD
          </button>
          <button
            className={activeTab === 'suggestions' ? 'tab active' : 'tab'}
            onClick={() => setActiveTab('suggestions')}
          >
            JD Suggestions
          </button>
        </div>

        {/* ================= ANALYSIS TAB ================= */}
        {activeTab === 'analyze' && (
          <>
            <div className="upload-box">
              {!resumeText ? (
                <div style={styles.uploadContainer}>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".txt,.pdf,.doc,.docx"
                    onChange={handleResumeFile}
                    style={{display: 'none'}}
                  />
                  <button
                    type="button"
                    style={styles.fileManagerBtn}
                    onClick={openFileManager}
                    title="Click to open file manager"
                  >
                    <span style={styles.plusIcon}>+</span>
                    <span style={styles.uploadText}>Open File Manager</span>
                  </button>
                  <p style={styles.uploadHint}>📄 Supported: .txt, .pdf, .doc, .docx</p>
                </div>
              ) : (
                <div className="file-pill">
                  📎 {resumeFileName}
                  <span
                    onClick={() => {
                      setResumeText('');
                      setResumeFileName('');
                      setAnalysis(null);
                      setMatchPercent(null);
                    }}
                    style={{cursor: 'pointer'}}
                  >
                    ✕
                  </span>
                </div>
              )}
            </div>

            {resumeText && (
              <form onSubmit={handleAnalyzeResume}>
                <label>📝 Job Description</label>
                <textarea
                  value={jobDescription}
                  onChange={(e) => setJobDescription(e.target.value)}
                  placeholder="Paste job description here..."
                />
                <button className="btn primary" disabled={loading}>
                  {loading ? 'Analyzing...' : 'Analyze Resume'}
                </button>
              </form>
            )}

            {analysis && matchPercent !== null && (
              <AnalysisUI analysis={analysis} matchPercent={matchPercent} />
            )}
          </>
        )}

        {/* ================= JD SUGGESTIONS TAB ================= */}
        {activeTab === 'suggestions' && (
          <form onSubmit={handleGetSuggestions}>
            <label>Job Description</label>
            <textarea
              value={jobDescription}
              onChange={(e) => setJobDescription(e.target.value)}
            />
            <button className="btn primary" disabled={loading}>
              {loading ? 'Generating...' : 'Generate Suggestions'}
            </button>

            {suggestions && (
              <JDSuggestionUI
                suggestions={suggestions}
                copyToClipboard={copyToClipboard}
              />
            )}
          </form>
        )}
      </div>
    </div>
  );
}

/* ================= ANALYSIS UI ================= */

const AnalysisUI = ({ analysis, matchPercent }) => (
  <div className="analysis-card">
    <h3>Match Score</h3>

    {/* BIG CLEAR % */}
    <div className="score-box">
      <span className="score">{matchPercent}%</span>
      <span className="label">
        {matchPercent >= 75
          ? 'Excellent Match'
          : matchPercent >= 50
          ? 'Moderate Match'
          : 'Low Match'}
      </span>
    </div>

    {/* PROGRESS BAR */}
    <div className="progress">
      <div
        className={`bar ${
          matchPercent >= 75 ? 'good' :
          matchPercent >= 50 ? 'mid' : 'low'
        }`}
        style={{ width: `${matchPercent}%` }}
      />
    </div>

    <SkillBlock title="Strengths" items={analysis.strengths || []} type="good" />
    <SkillBlock title="Missing Skills" items={analysis.missingSkills || []} type="warn" />

    <h4>AI Recommendation</h4>
    <div className="recommendation">
      {analysis.recommendation || 'No recommendation provided.'}
    </div>
  </div>
);

const SkillBlock = ({ title, items, type }) => (
  <>
    <h4>{title}</h4>
    <div className="tags">
      {items.map((i, idx) => (
        <span key={idx} className={`tag ${type}`}>{i}</span>
      ))}
    </div>
  </>
);

/* ================= JD SUGGESTIONS ================= */

const JDSuggestionUI = ({ suggestions, copyToClipboard }) => (
  <div className="analysis-card">
    <h3>JD Search Helper</h3>

    <button
      className="btn outline"
      onClick={() => copyToClipboard(JSON.stringify(suggestions, null, 2))}
    >
      Copy All Keywords
    </button>

    <button
      className="btn success"
      onClick={() => copyToClipboard(suggestions.booleanSearch)}
    >
      Copy Boolean Search
    </button>

    <TagGroup title="Job Titles" items={suggestions.jobTitles || []} />
    <TagGroup title="Primary Skills" items={suggestions.primarySkills || []} />
    <TagGroup title="Secondary Skills" items={suggestions.secondarySkills || []} />
    <TagGroup title="Tools & Technologies" items={suggestions.toolsAndTechnologies || []} />

    <h4>Boolean Search</h4>
    <div className="recommendation">{suggestions.booleanSearch}</div>
  </div>
);

const TagGroup = ({ title, items }) => (
  <>
    <h4>{title}</h4>
    <div className="tags">
      {items.map((i, idx) => (
        <span key={idx} className="tag neutral">{i}</span>
      ))}
    </div>
  </>
);

/* ================= STYLES ================= */

const page = {
  minHeight: '100vh',
  background: 'linear-gradient(135deg, #0F172A, #1E3A8A)',
  padding: '28px',
  fontFamily: 'Inter, sans-serif',
  color: '#FFFFFF'
};

const css = `
.card {
  background: white;
  color: #0F172A;
  padding: 26px;
  border-radius: 18px;
  box-shadow: 0 15px 32px rgba(0,0,0,0.2);
}

.subtitle {
  font-size: 14px;
  color: #64748B;
  margin-bottom: 18px;
}

.tabs {
  display: flex;
  border-bottom: 2px solid #E5E7EB;
  margin-bottom: 20px;
}

.tab {
  padding: 12px 20px;
  background: transparent;
  border: none;
  font-weight: 600;
  cursor: pointer;
  color: #64748B;
}

.tab.active {
  color: #1E40AF;
  border-bottom: 3px solid #1E40AF;
}

.upload-box {
  margin-bottom: 16px;
  animation: fadeInUp 0.5s ease-out;
}

.file-pill {
  background: linear-gradient(135deg, #DBEAFE, #E0F2FE);
  color: #1E40AF;
  padding: 12px 18px;
  border-radius: 20px;
  display: inline-flex;
  align-items: center;
  gap: 10px;
  font-weight: 600;
  box-shadow: 0 4px 12px rgba(30, 64, 175, 0.15);
  animation: scaleIn 0.3s ease-out;
}

.file-pill span {
  cursor: pointer;
  font-weight: bold;
  transition: all 0.2s ease;
  padding: 4px 8px;
  border-radius: 8px;
}

.file-pill span:hover {
  background: rgba(30, 64, 175, 0.2);
  transform: scale(1.2);
}

textarea {
  width: 100%;
  min-height: 180px;
  padding: 12px;
  border-radius: 10px;
  border: 2px solid #E2E8F0;
  margin-bottom: 14px;
  font-family: 'Inter', sans-serif;
  transition: all 0.3s ease;
}

textarea:focus {
  outline: none;
  border-color: #1E40AF;
  box-shadow: 0 0 0 3px rgba(30, 64, 175, 0.1);
}

.btn {
  padding: 10px 16px;
  border-radius: 10px;
  border: none;
  cursor: pointer;
  font-weight: 600;
  margin-right: 10px;
}

.primary {
  background: #1E40AF;
  color: white;
}

.outline {
  background: white;
  border: 1px solid #1E40AF;
  color: #1E40AF;
}

.success {
  background: #16A34A;
  color: white;
}

.analysis-card {
  margin-top: 26px;
  background: #F8FAFC;
  padding: 22px;
  border-radius: 16px;
}

.score-box {
  display: flex;
  align-items: baseline;
  gap: 12px;
  margin-bottom: 12px;
}

.score {
  font-size: 42px;
  font-weight: 800;
  color: #1E40AF;
}

.label {
  font-size: 14px;
  color: #64748B;
  font-weight: 600;
}

.progress {
  background: #E5E7EB;
  height: 16px;
  border-radius: 10px;
  overflow: hidden;
  margin-bottom: 18px;
}

.bar {
  height: 100%;
}

.bar.good { background: #16A34A; }
.bar.mid { background: #F59E0B; }
.bar.low { background: #DC2626; }

.tags {
  margin-bottom: 14px;
}

.tag {
  padding: 6px 12px;
  border-radius: 14px;
  font-size: 13px;
  margin-right: 6px;
  display: inline-block;
}

.tag.good { background: #DCFCE7; color: #166534; }
.tag.warn { background: #FEF3C7; color: #92400E; }
.tag.neutral { background: #E5E7EB; }

.recommendation {
  background: #E0F2FE;
  padding: 12px;
  border-radius: 10px;
  margin-top: 8px;
}

@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes scaleIn {
  from {
    opacity: 0;
    transform: scale(0.95);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
}

@keyframes float {
  0%, 100% {
    transform: translateY(0px);
  }
  50% {
    transform: translateY(-8px);
  }
}
`;

// import React, { useState } from 'react';
// import { aiAPI } from '../api/client';
// import { toast } from 'react-toastify';

// export default function AIAssistant() {
//   const [resumeText, setResumeText] = useState('');
//   const [resumeFileName, setResumeFileName] = useState('');
//   const [jobDescription, setJobDescription] = useState('');
//   const [analysis, setAnalysis] = useState(null);
//   const [matchPercent, setMatchPercent] = useState(null);
//   const [suggestions, setSuggestions] = useState(null);
//   const [loading, setLoading] = useState(false);
//   const [activeTab, setActiveTab] = useState('analyze');

//   // ================= Copy Helper =================
//   const copyToClipboard = async (text) => {
//     try {
//       await navigator.clipboard.writeText(text);
//       toast.success('Copied to clipboard');
//     } catch {
//       toast.error('Failed to copy');
//     }
//   };

//   // ================= Resume Analysis =================
//   const handleAnalyzeResume = async (e) => {
//     e.preventDefault();

//     if (!resumeText) return toast.error('Please upload a resume');
//     if (!jobDescription) return toast.error('Please provide a job description');

//     setLoading(true);
//     try {
//       const response = await aiAPI.analyzeResume({
//         resumeText,
//         jobDescription,
//       });

//       // ✅ FIX: backend already returns parsed JSON
//       const parsed = response.data.analysis;

//       setAnalysis(parsed);
//       setMatchPercent(Math.round(parsed.matchScore * 100));
//     } catch (err) {
//       console.error(err);
//       toast.error('Failed to analyze resume');
//       setAnalysis(null);
//       setMatchPercent(null);
//     } finally {
//       setLoading(false);
//     }
//   };

//   // ================= JD Suggestions =================
//   const handleGetSuggestions = async (e) => {
//     e.preventDefault();
//     if (!jobDescription) return toast.error('Please provide a job description');

//     setLoading(true);
//     try {
//       const response = await aiAPI.getJDSuggestions({
//         jobDescription,
//       });

//       // ✅ Already parsed in backend
//       setSuggestions(response.data.suggestions);
//     } catch (err) {
//       console.error(err);
//       toast.error('Failed to get JD suggestions');
//       setSuggestions(null);
//     } finally {
//       setLoading(false);
//     }
//   };

//   // ================= Resume File Upload =================
//   const handleResumeFile = (e) => {
//     const file = e.target.files[0];
//     if (!file) return;

//     setResumeFileName(file.name);

//     const reader = new FileReader();
//     reader.onload = (event) => setResumeText(event.target.result);
//     reader.onerror = () => toast.error('Failed to read file');
//     reader.readAsText(file);
//   };

//   return (
//     <div className="card" style={{ padding: '1.5rem' }}>
//       <h2>AI Assistant</h2>

//       {/* ================= Tabs ================= */}
//       <div style={{ marginBottom: 20, borderBottom: '2px solid #e0e0e0' }}>
//         <button
//           onClick={() => setActiveTab('analyze')}
//           style={tabStyle(activeTab === 'analyze')}
//         >
//           Resume vs JD Analysis
//         </button>
//         <button
//           onClick={() => setActiveTab('suggestions')}
//           style={tabStyle(activeTab === 'suggestions')}
//         >
//           JD Suggestions
//         </button>
//       </div>

//       {/* ================= Resume Analysis ================= */}
//       {activeTab === 'analyze' && (
//         <>
//           <div className="form-group">
//             {!resumeText ? (
//               <>
//                 <label>Upload Resume (DOCX / PDF)</label>
//                 <input
//                   type="file"
//                   accept=".txt,.pdf,.doc,.docx"
//                   onChange={handleResumeFile}
//                 />
//               </>
//             ) : (
//               <div>
//                 <strong>Uploaded:</strong> {resumeFileName}
//                 <button
//                   onClick={() => {
//                     setResumeText('');
//                     setResumeFileName('');
//                     setAnalysis(null);
//                   }}
//                   style={{ marginLeft: 10 }}
//                 >
//                   Remove
//                 </button>
//               </div>
//             )}
//           </div>

//           {resumeText && (
//             <form onSubmit={handleAnalyzeResume}>
//               <label>Job Description</label>
//               <textarea
//                 value={jobDescription}
//                 onChange={(e) => setJobDescription(e.target.value)}
//                 style={{ minHeight: 200 }}
//               />
//               <button type="submit" disabled={loading}>
//                 {loading ? 'Analyzing...' : 'Analyze Resume'}
//               </button>
//             </form>
//           )}

//           {analysis && (
//             <AnalysisUI
//               analysis={analysis}
//               matchPercent={matchPercent}
//             />
//           )}
//         </>
//       )}

//       {/* ================= JD Suggestions ================= */}
//       {activeTab === 'suggestions' && (
//         <form onSubmit={handleGetSuggestions}>
//           <label>Job Description</label>
//           <textarea
//             value={jobDescription}
//             onChange={(e) => setJobDescription(e.target.value)}
//             style={{ minHeight: 200 }}
//           />
//           <button type="submit" disabled={loading}>
//             {loading ? 'Generating...' : 'Get JD Suggestions'}
//           </button>

//           {suggestions && (
//             <JDSuggestionUI
//               suggestions={suggestions}
//               copyToClipboard={copyToClipboard}
//             />
//           )}
//         </form>
//       )}
//     </div>
//   );
// }

// /* ================= Styles ================= */

// const tabStyle = (active) => ({
//   padding: '0.75rem 1.5rem',
//   border: 'none',
//   background: active ? '#3498db' : 'transparent',
//   color: active ? '#fff' : '#666',
//   cursor: 'pointer'
// });

// /* ================= Analysis UI ================= */

// const AnalysisUI = ({ analysis, matchPercent }) => (
//   <div
//     style={{
//       marginTop: 20,
//       background: '#f8fbff',
//       padding: 16,
//       borderRadius: 6
//     }}
//   >
//     <h3>Analysis Result</h3>

//     <strong>Match Score</strong>
//     <div style={{ background: '#ddd', height: 20, borderRadius: 10 }}>
//       <div
//         style={{
//           width: `${matchPercent}%`,
//           height: '100%',
//           background:
//             matchPercent >= 75
//               ? '#28a745'
//               : matchPercent >= 50
//               ? '#ffc107'
//               : '#dc3545',
//           color: '#fff',
//           textAlign: 'center'
//         }}
//       >
//         {matchPercent}%
//       </div>
//     </div>

//     <SkillSection
//       title="Strengths"
//       items={analysis.strengths}
//       color="#d4edda"
//     />

//     <SkillSection
//       title="Missing Skills"
//       items={analysis.missingSkills}
//       color="#fff3cd"
//     />

//     <strong>AI Recommendation</strong>
//     <div
//       style={{
//         background: '#e7f1ff',
//         padding: 10,
//         marginTop: 6
//       }}
//     >
//       {analysis.recommendation}
//     </div>
//   </div>
// );

// const SkillSection = ({ title, items, color }) => (
//   <>
//     <strong>{title}</strong>
//     <div>
//       {items.map((i, idx) => (
//         <span
//           key={idx}
//           style={{
//             background: color,
//             padding: '6px 10px',
//             borderRadius: 12,
//             marginRight: 6,
//             display: 'inline-block'
//           }}
//         >
//           {i}
//         </span>
//       ))}
//     </div>
//   </>
// );

// /* ================= JD Suggestions UI ================= */

// const JDSuggestionUI = ({ suggestions, copyToClipboard }) => {
//   const allText = `
// Job Titles:
// ${suggestions.jobTitles.join(', ')}

// Primary Skills:
// ${suggestions.primarySkills.join(', ')}

// Secondary Skills:
// ${suggestions.secondarySkills.join(', ')}

// Tools & Technologies:
// ${suggestions.toolsAndTechnologies.join(', ')}

// Boolean Search:
// ${suggestions.booleanSearch}
//   `.trim();

//   return (
//     <div
//       style={{
//         marginTop: 20,
//         background: '#f0f7ff',
//         padding: 16,
//         borderRadius: 6
//       }}
//     >
//       <h3>Search Helper</h3>

//       <button onClick={() => copyToClipboard(allText)} style={btn('#3498db')}>
//         Copy All Keywords
//       </button>

//       <button
//         onClick={() => copyToClipboard(suggestions.booleanSearch)}
//         style={btn('#28a745')}
//       >
//         Copy Boolean Search
//       </button>

//       <br />

//       <TagGroup title="Job Titles" items={suggestions.jobTitles} />
//       <br />
//       <TagGroup title="Primary Skills" items={suggestions.primarySkills} />
//       <br />
//       <TagGroup title="Secondary Skills" items={suggestions.secondarySkills} />
//       <br />
//       <TagGroup title="Tools & Technologies" items={suggestions.toolsAndTechnologies} />

//       <strong>Boolean Search</strong>
//       <div style={{ background: '#e2e3e5', padding: 10, marginTop: 6 }}>
//         {suggestions.booleanSearch}
//       </div>
//     </div>
//   );
// };

// const btn = (bg) => ({
//   marginRight: 10,
//   marginBottom: 10,
//   padding: '6px 12px',
//   background: bg,
//   color: '#090909ff',
//   border: 'none',
//   borderRadius: 4,
//   cursor: 'pointer'
// });

// const TagGroup = ({ title, items }) => (
//   <>
//     <strong>{title}</strong>
//     <br />
//     <div>
//       {items.map((item, idx) => (
//         <span
//           key={idx}
//           style={{
//             color: '#000000ff',
//             padding: '6px 10px',
//             borderRadius: 12,
//             marginRight: 6,
//             display: 'inline-block'
//           }}
//         >
//           {item}
//         </span>
//       ))}
//     </div>
//   </>
// );


// // import React, { useState } from 'react';
// // import { aiAPI } from '../api/client';
// // import { toast } from 'react-toastify';

// // export default function AIAssistant() {
// //   const [resumeText, setResumeText] = useState('');
// //   const [resumeFileName, setResumeFileName] = useState('');
// //   const [jobDescription, setJobDescription] = useState('');
// //   const [analysis, setAnalysis] = useState(null);
// //   const [matchPercent, setMatchPercent] = useState(null);
// //   const [suggestions, setSuggestions] = useState(null);
// //   const [loading, setLoading] = useState(false);
// //   const [activeTab, setActiveTab] = useState('analyze');

// //   // ================= Copy Helper =================
// //   const copyToClipboard = async (text) => {
// //     try {
// //       await navigator.clipboard.writeText(text);
// //       toast.success('Copied to clipboard');
// //     } catch {
// //       toast.error('Failed to copy');
// //     }
// //   };

// //   // ================= Resume Analysis =================
// //   const handleAnalyzeResume = async (e) => {
// //     e.preventDefault();

// //     if (!resumeText) return toast.error('Please upload a resume');
// //     if (!jobDescription) return toast.error('Please provide a job description');

// //     setLoading(true);
// //     try {
// //       const response = await aiAPI.analyzeResume({ resumeText, jobDescription });

// //       const raw = response.data.analysis;
// //       const clean = raw.replace(/```json|```/g, '').trim();
// //       const parsed = JSON.parse(clean);

// //       setAnalysis(parsed);
// //       setMatchPercent(Math.round(parsed.matchScore * 100));
// //     } catch (err) {
// //       console.error(err);
// //       toast.error('Failed to analyze resume');
// //       setAnalysis(null);
// //       setMatchPercent(null);
// //     } finally {
// //       setLoading(false);
// //     }
// //   };

// //   // ================= JD Suggestions =================
// //   const handleGetSuggestions = async (e) => {
// //     e.preventDefault();
// //     if (!jobDescription) return toast.error('Please provide a job description');

// //     setLoading(true);
// //     try {
// //       const response = await aiAPI.getJDSuggestions({ jobDescription });
// //       setSuggestions(response.data.suggestions);
// //     } catch (err) {
// //       console.error(err);
// //       toast.error('Failed to get JD suggestions');
// //       setSuggestions(null);
// //     } finally {
// //       setLoading(false);
// //     }
// //   };

// //   // ================= Resume File Upload =================
// //   const handleResumeFile = (e) => {
// //     const file = e.target.files[0];
// //     if (!file) return;

// //     setResumeFileName(file.name);

// //     const reader = new FileReader();
// //     reader.onload = (event) => setResumeText(event.target.result);
// //     reader.onerror = () => toast.error('Failed to read file');
// //     reader.readAsText(file);
// //   };

// //   return (
// //     <div className="card" style={{ padding: '1.5rem' }}>
// //       <h2>AI Assistant</h2>

// //       {/* ================= Tabs ================= */}
// //       <div style={{ marginBottom: 20, borderBottom: '2px solid #e0e0e0' }}>
// //         <button onClick={() => setActiveTab('analyze')} style={tabStyle(activeTab === 'analyze')}>
// //           Resume vs JD Analysis
// //         </button>
// //         <button onClick={() => setActiveTab('suggestions')} style={tabStyle(activeTab === 'suggestions')}>
// //           JD Suggestions
// //         </button>
// //       </div>

// //       {/* ================= Resume Analysis ================= */}
// //       {activeTab === 'analyze' && (
// //         <>
// //           <div className="form-group">
// //             {!resumeText ? (
// //               <>
// //                 <label>Upload Resume (DOCX / PDF)</label>
// //                 <input type="file" accept=".txt,.pdf,.doc,.docx" onChange={handleResumeFile} />
// //               </>
// //             ) : (
// //               <div>
// //                 <strong>Uploaded:</strong> {resumeFileName}
// //                 <button onClick={() => {
// //                   setResumeText('');
// //                   setResumeFileName('');
// //                   setAnalysis(null);
// //                 }} style={{ marginLeft: 10 }}>
// //                   Remove
// //                 </button>
// //               </div>
// //             )}
// //           </div>

// //           {resumeText && (
// //             <form onSubmit={handleAnalyzeResume}>
// //               <label>Job Description</label>
// //               <textarea
// //                 value={jobDescription}
// //                 onChange={(e) => setJobDescription(e.target.value)}
// //                 style={{ minHeight: 200 }}
// //               />
// //               <button type="submit" disabled={loading}>
// //                 {loading ? 'Analyzing...' : 'Analyze Resume'}
// //               </button>
// //             </form>
// //           )}

// //           {analysis && <AnalysisUI analysis={analysis} matchPercent={matchPercent} />}
// //         </>
// //       )}

// //       {/* ================= JD Suggestions ================= */}
// //       {activeTab === 'suggestions' && (
// //         <form onSubmit={handleGetSuggestions}>
// //           <label>Job Description</label>
// //           <textarea
// //             value={jobDescription}
// //             onChange={(e) => setJobDescription(e.target.value)}
// //             style={{ minHeight: 200 }}
// //           />
// //           <button type="submit" disabled={loading}>
// //             {loading ? 'Generating...' : 'Get JD Suggestions'}
// //           </button>

// //           {suggestions && (
// //             <JDSuggestionUI
// //               suggestions={suggestions}
// //               copyToClipboard={copyToClipboard}
// //             />
// //           )}
// //         </form>
// //       )}
// //     </div>
// //   );
// // }

// // /* ================= Styles ================= */

// // const tabStyle = (active) => ({
// //   padding: '0.75rem 1.5rem',
// //   border: 'none',
// //   background: active ? '#3498db' : 'transparent',
// //   color: active ? '#fff' : '#666',
// //   cursor: 'pointer'
// // });

// // /* ================= Analysis UI ================= */

// // const AnalysisUI = ({ analysis, matchPercent }) => (
// //   <div style={{ marginTop: 20, background: '#f8fbff', padding: 16, borderRadius: 6 }}>
// //     <h3>Analysis Result</h3>

// //     <strong>Match Score</strong>
// //     <div style={{ background: '#ddd', height: 20, borderRadius: 10 }}>
// //       <div style={{
// //         width: `${matchPercent}%`,
// //         height: '100%',
// //         background: matchPercent >= 75 ? '#28a745' : matchPercent >= 50 ? '#ffc107' : '#dc3545',
// //         color: '#fff',
// //         textAlign: 'center'
// //       }}>
// //         {matchPercent}%
// //       </div>
// //     </div>

// //     <SkillSection title="Strengths" items={analysis.strengths} color="#d4edda" />
// //     <SkillSection title="Missing Skills" items={analysis.missingSkills} color="#fff3cd" />

// //     <strong>AI Recommendation</strong>
// //     <div style={{ background: '#e7f1ff', padding: 10, marginTop: 6 }}>
// //       {analysis.recommendation}
// //     </div>
// //   </div>
// // );

// // const SkillSection = ({ title, items, color }) => (
// //   <>
// //     <strong>{title}</strong>
// //     <div>
// //       {items.map((i, idx) => (
// //         <span key={idx} style={{
// //           background: color,
// //           padding: '6px 10px',
// //           borderRadius: 12,
// //           marginRight: 6,
// //           display: 'inline-block'
// //         }}>
// //           {i}
// //         </span>
// //       ))}
// //     </div>
// //   </>
// // );

// // /* ================= JD Suggestions UI ================= */

// // const JDSuggestionUI = ({ suggestions, copyToClipboard }) => {
// //   const allText = `
// // Job Titles:
// // ${suggestions.jobTitles.join(', ')}

// // Primary Skills:
// // ${suggestions.primarySkills.join(', ')}

// // Secondary Skills:
// // ${suggestions.secondarySkills.join(', ')}

// // Tools & Technologies:
// // ${suggestions.toolsAndTechnologies.join(', ')}

// // Boolean Search:
// // ${suggestions.booleanSearch}
// //   `.trim();

// //   return (
// //     <div style={{ marginTop: 20, background: '#f0f7ff', padding: 16, borderRadius: 6 }}>
// //       <h3>Search Helper</h3>

// //       <button onClick={() => copyToClipboard(allText)} style={btn('#3498db')}>
// //         Copy All Keywords
// //       </button>

// //       <button onClick={() => copyToClipboard(suggestions.booleanSearch)} style={btn('#28a745')}>
// //         Copy Boolean Search
// //       </button>
// //       <br />

// //       <TagGroup title="Job Titles" items={suggestions.jobTitles} bg="#007bff" />
// //       <br />
// //       <TagGroup title="Primary Skills" 
// //       items={suggestions.primarySkills} bg="#28a745" />
// //             <br />

// //       <TagGroup title="Secondary Skills" items={suggestions.secondarySkills} bg="#ffc107" />
// //             <br />

// //       <TagGroup title="Tools & Technologies" items={suggestions.toolsAndTechnologies} bg="#17a2b8" />
// //             <br />


// //       <strong>Boolean Search</strong>
// //       <div style={{ background: '#e2e3e5', padding: 10, marginTop: 6 }}>
// //         {suggestions.booleanSearch}
// //       </div>
// //     </div>
// //   );
// // };

// // const btn = (bg) => ({
// //   marginRight: 10,
// //   marginBottom: 10,
// //   padding: '6px 12px',
// //   background: bg,
// //   color: '#090909ff',
// //   border: 'none',
// //   borderRadius: 4,
// //   cursor: 'pointer'
// // });

// // const TagGroup = ({ title, items, bg }) => (
// //   <>
// //     <strong>{title}</strong>
// //     <br />
// //     <div>
// //       {items.map((item, idx) => (
// //         <span key={idx} style={{
// //           // background: bg,
// //           color: '#000000ff',
// //           padding: '6px 10px',
// //           borderRadius: 12,
// //           marginRight: 6,
// //           display: 'inline-block'
// //         }}>
// //           {item}
// //         </span>
// //       ))}
// //     </div>
// //   </>
// // );
// // // import React, { useState } from 'react';
// // // import { aiAPI } from '../api/client';
// // // import { toast } from 'react-toastify';

// // // export default function AIAssistant() {
// // //   const [resumeText, setResumeText] = useState('');
// // //   const [resumeFileName, setResumeFileName] = useState('');
// // //   const [jobDescription, setJobDescription] = useState('');
// // //   const [analysis, setAnalysis] = useState(null);
// // //   const [matchPercent, setMatchPercent] = useState(null);
// // //   const [suggestions, setSuggestions] = useState(null);
// // //   const [loading, setLoading] = useState(false);
// // //   const [activeTab, setActiveTab] = useState('analyze');

// // //   // ================= Resume Analysis =================
// // //   const handleAnalyzeResume = async (e) => {
// // //     e.preventDefault();

// // //     if (!resumeText) {
// // //       toast.error('Please upload a resume');
// // //       return;
// // //     }
// // //     if (!jobDescription) {
// // //       toast.error('Please provide a job description');
// // //       return;
// // //     }

// // //     setLoading(true);
// // //     try {
// // //       const response = await aiAPI.analyzeResume({
// // //         resumeText,
// // //         jobDescription
// // //       });

// // //       const raw = response.data.analysis;
// // //       const clean = raw.replace(/```json|```/g, '').trim();
// // //       const parsed = JSON.parse(clean);

// // //       setAnalysis(parsed);
// // //       setMatchPercent(Math.round(parsed.matchScore * 100));

// // //     } catch (error) {
// // //       console.error(error);
// // //       toast.error('Failed to analyze resume');
// // //       setAnalysis(null);
// // //       setMatchPercent(null);
// // //     } finally {
// // //       setLoading(false);
// // //     }
// // //   };

// // //   // ================= JD Suggestions =================
// // //   const handleGetSuggestions = async (e) => {
// // //     e.preventDefault();

// // //     if (!jobDescription) {
// // //       toast.error('Please provide a job description');
// // //       return;
// // //     }

// // //     setLoading(true);
// // //     try {
// // //       const response = await aiAPI.getJDSuggestions({
// // //         jobDescription
// // //       });

// // //       setSuggestions(response.data.suggestions);

// // //     } catch (error) {
// // //       console.error(error);
// // //       toast.error('Failed to get JD suggestions');
// // //       setSuggestions(null);
// // //     } finally {
// // //       setLoading(false);
// // //     }
// // //   };

// // //   // ================= Resume File Upload =================
// // //   const handleResumeFile = (e) => {
// // //     const file = e.target.files[0];
// // //     if (!file) return;

// // //     setResumeFileName(file.name);

// // //     const reader = new FileReader();
// // //     reader.onload = (event) => {
// // //       setResumeText(event.target.result);
// // //     };
// // //     reader.onerror = () => toast.error('Failed to read file');
// // //     reader.readAsText(file);
// // //   };

// // //   return (
// // //     <div className="card" style={{ padding: '1.5rem' }}>
// // //       <h2>AI Assistant</h2>

// // //       {/* ================= Tabs ================= */}
// // //       <div style={{ marginBottom: '1.5rem', borderBottom: '2px solid #e0e0e0' }}>
// // //         <button
// // //           onClick={() => { setActiveTab('analyze'); setAnalysis(null); }}
// // //           style={tabStyle(activeTab === 'analyze')}
// // //         >
// // //           Resume vs JD Analysis
// // //         </button>

// // //         <button
// // //           onClick={() => { setActiveTab('suggestions'); setSuggestions(null); }}
// // //           style={tabStyle(activeTab === 'suggestions')}
// // //         >
// // //           JD Suggestions
// // //         </button>
// // //       </div>

// // //       {/* ================= Resume vs JD ================= */}
// // //       {activeTab === 'analyze' && (
// // //         <>
// // //           <div className="form-group" style={{ marginBottom: '1rem' }}>
// // //             {!resumeText ? (
// // //               <>
// // //                 <label>Upload Resume File: [* Please upload a valid resume file in Docx format]</label>
// // //                 <input type="file" accept=".txt,.pdf,.doc,.docx" onChange={handleResumeFile} />
// // //                 <small style={{ color: '#888' }}>Supported: .txt, .pdf, .docx</small>
// // //               </>
// // //             ) : (
// // //               <div>
// // //                 <strong>Uploaded Resume:</strong> {resumeFileName}
// // //                 <button
// // //                   type="button"
// // //                   style={{ marginLeft: 16 }}
// // //                   onClick={() => {
// // //                     setResumeText('');
// // //                     setResumeFileName('');
// // //                     setAnalysis(null);
// // //                     setMatchPercent(null);
// // //                   }}
// // //                 >
// // //                   Remove
// // //                 </button>
// // //               </div>
// // //             )}
// // //           </div>

// // //           {resumeText && (
// // //             <form onSubmit={handleAnalyzeResume}>
// // //               <div className="form-group">
// // //                 <label>Job Description</label>
// // //                 <textarea
// // //                   value={jobDescription}
// // //                   onChange={(e) => setJobDescription(e.target.value)}
// // //                   placeholder="Paste job description here..."
// // //                   style={{ minHeight: 200 }}
// // //                 />
// // //               </div>

// // //               <button type="submit" disabled={loading}>
// // //                 {loading ? 'Analyzing...' : 'Analyze Resume'}
// // //               </button>
// // //             </form>
// // //           )}

// // //           {analysis && (
// // //             <AnalysisUI analysis={analysis} matchPercent={matchPercent} />
// // //           )}
// // //         </>
// // //       )}

// // //       {/* ================= JD Suggestions ================= */}
// // //       {activeTab === 'suggestions' && (
// // //         <form onSubmit={handleGetSuggestions}>
// // //           <div className="form-group">
// // //             <label>Job Description</label>
// // //             <textarea
// // //               value={jobDescription}
// // //               onChange={(e) => setJobDescription(e.target.value)}
// // //               placeholder="Paste job description here..."
// // //               style={{ minHeight: 200 }}
// // //             />
// // //           </div>

// // //           <button type="submit" disabled={loading}>
// // //             {loading ? 'Generating...' : 'Get JD Suggestions'}
// // //           </button>

// // //           {suggestions && <JDSuggestionUI suggestions={suggestions} />}
// // //         </form>
// // //       )}
// // //     </div>
// // //   );
// // // }

// // // /* ================= UI COMPONENTS ================= */

// // // const tabStyle = (active) => ({
// // //   padding: '0.75rem 1.5rem',
// // //   border: 'none',
// // //   background: active ? '#3498db' : 'transparent',
// // //   color: active ? 'white' : '#666',
// // //   cursor: 'pointer',
// // //   fontSize: '1rem'
// // // });

// // // const AnalysisUI = ({ analysis, matchPercent }) => (
// // //   <div style={{ marginTop: '2rem', padding: '1.5rem', background: '#f8fbff', borderRadius: 6 }}>
// // //     <h3>Analysis Result</h3>

// // //     <strong>Match Score</strong>
// // //     <div style={{ background: '#ddd', height: 20, borderRadius: 10, margin: '6px 0 20px' }}>
// // //       <div style={{
// // //         width: `${matchPercent}%`,
// // //         height: '100%',
// // //         background: matchPercent >= 75 ? '#28a745' : matchPercent >= 50 ? '#ffc107' : '#dc3545',
// // //         color: '#fff',
// // //         textAlign: 'center',
// // //         fontSize: 12,
// // //         lineHeight: '20px'
// // //       }}>
// // //         {matchPercent}%
// // //       </div>
// // //     </div>

// // //     <SkillSection title="Strengths" items={analysis.strengths} color="#d4edda" />
// // //     <SkillSection title="Missing / Improvement Areas" items={analysis.missingSkills} color="#fff3cd" />

// // //     <strong>AI Recommendation</strong>
// // //     <div style={{ background: '#e7f1ff', padding: 10, borderRadius: 4, marginTop: 6 }}>
// // //       {analysis.recommendation}
// // //     </div>
// // //   </div>
// // // );

// // // const SkillSection = ({ title, items, color }) => (
// // //   <>
// // //     <strong>{title}</strong>
// // //     <div style={{ margin: '6px 0 16px' }}>
// // //       {items.map((item, idx) => (
// // //         <span key={idx} style={{
// // //           display: 'inline-block',
// // //           background: color,
// // //           padding: '6px 10px',
// // //           borderRadius: 12,
// // //           fontSize: 13,
// // //           marginRight: 6,
// // //           marginBottom: 6
// // //         }}>
// // //           {item}
// // //         </span>
// // //       ))}
// // //     </div>
// // //   </>
// // // );

// // // const JDSuggestionUI = ({ suggestions }) => (
// // //   <div style={{ marginTop: '2rem', padding: '1.5rem', background: '#f0f7ff', borderRadius: 6 }}>
// // //     <h3>Naukri Search Helper</h3>

// // //     <TagGroup title="Job Titles" items={suggestions.jobTitles} bg="#007bff" />
// // //     <TagGroup title="Primary Skills" items={suggestions.primarySkills} bg="#28a745" />
// // //     <TagGroup title="Secondary Skills" items={suggestions.secondarySkills} bg="#ffc107" />
// // //     <TagGroup title="Tools & Technologies" items={suggestions.toolsAndTechnologies} bg="#17a2b8" />

// // //     <strong>Naukri Boolean Search</strong>
// // //     <div style={{ background: '#e2e3e5', padding: 10, borderRadius: 4, marginTop: 6 }}>
// // //       {suggestions.booleanSearch}
// // //     </div>
// // //   </div>
// // // );

// // // const TagGroup = ({ title, items, bg }) => (
// // //   <>
// // //     {/* <strongstrong>{title}</strong> */}
// // //     <div style={{ margin: '6px 0 16px' }}>
// // //       {items.map((item, idx) => (
// // //         <span key={idx} style={{
// // //           display: 'inline-block',
// // //           background: bg,
// // //           color: '#fff',
// // //           padding: '6px 10px',
// // //           borderRadius: 12,
// // //           fontSize: 13,
// // //           marginRight: 6,
// // //           marginBottom: 6
// // //         }}>
// // //           {item}
// // //         </span>
// // //       ))}
// // //     </div>
// // //   </>
// // // );

// // // // import React, { useState } from 'react';
// // // // import { aiAPI } from '../api/client';
// // // // import { toast } from 'react-toastify';

// // // // export default function AIAssistant() {
// // // //   const [resumeText, setResumeText] = useState('');
// // // //   const [resumeFileName, setResumeFileName] = useState('');
// // // //   const [jobDescription, setJobDescription] = useState('');
// // // //   const [analysis, setAnalysis] = useState(null);
// // // //   const [matchPercent, setMatchPercent] = useState(null);
// // // //   const [suggestions, setSuggestions] = useState('');
// // // //   const [loading, setLoading] = useState(false);
// // // //   const [activeTab, setActiveTab] = useState('analyze');

// // // //   // ================= Resume Analysis =================
// // // //   const handleAnalyzeResume = async (e) => {
// // // //     e.preventDefault();

// // // //     if (!resumeText) {
// // // //       toast.error('Please upload a resume');
// // // //       return;
// // // //     }
// // // //     if (!jobDescription) {
// // // //       toast.error('Please provide a job description');
// // // //       return;
// // // //     }

// // // //     setLoading(true);
// // // //     try {
// // // //       const response = await aiAPI.analyzeResume({
// // // //         resumeText,
// // // //         jobDescription
// // // //       });

// // // //       // 🔹 Clean & parse Gemini JSON
// // // //       const raw = response.data.analysis;
// // // //       const clean = raw.replace(/```json|```/g, '').trim();
// // // //       const parsed = JSON.parse(clean);

// // // //       setAnalysis(parsed);
// // // //       setMatchPercent(Math.round(parsed.matchScore * 100));

// // // //     } catch (error) {
// // // //       console.error(error);
// // // //       toast.error('Failed to analyze resume');
// // // //       setAnalysis(null);
// // // //       setMatchPercent(null);
// // // //     } finally {
// // // //       setLoading(false);
// // // //     }
// // // //   };

// // // //   // ================= JD Suggestions =================
// // // //   const handleGetSuggestions = async (e) => {
// // // //     e.preventDefault();

// // // //     if (!jobDescription) {
// // // //       toast.error('Please provide a job description');
// // // //       return;
// // // //     }

// // // //     setLoading(true);
// // // //     try {
// // // //       const response = await aiAPI.getJDSuggestions({
// // // //         jobDescription
// // // //       });
// // // //       setSuggestions(response.data.suggestions);
// // // //     } catch (error) {
// // // //       toast.error('Failed to get suggestions');
// // // //     } finally {
// // // //       setLoading(false);
// // // //     }
// // // //   };

// // // //   // ================= Resume File Upload =================
// // // //   const handleResumeFile = (e) => {
// // // //     const file = e.target.files[0];
// // // //     if (!file) return;

// // // //     setResumeFileName(file.name);

// // // //     const reader = new FileReader();
// // // //     reader.onload = (event) => {
// // // //       setResumeText(event.target.result);
// // // //     };
// // // //     reader.onerror = () => toast.error('Failed to read file');
// // // //     reader.readAsText(file);
// // // //   };

// // // //   return (
// // // //     <div className="card" style={{ padding: '1.5rem' }}>
// // // //       <h2>AI Assistant</h2>

// // // //       {/* ================= Tabs ================= */}
// // // //       <div style={{ marginBottom: '1.5rem', borderBottom: '2px solid #e0e0e0' }}>
// // // //         <button
// // // //           onClick={() => { setActiveTab('analyze'); setAnalysis(null); }}
// // // //           style={{
// // // //             padding: '0.75rem 1.5rem',
// // // //             border: 'none',
// // // //             background: activeTab === 'analyze' ? '#3498db' : 'transparent',
// // // //             color: activeTab === 'analyze' ? 'white' : '#666',
// // // //             cursor: 'pointer',
// // // //             fontSize: '1rem'
// // // //           }}
// // // //         >
// // // //           Resume vs JD Analysis
// // // //         </button>

// // // //         <button
// // // //           onClick={() => { setActiveTab('suggestions'); setSuggestions(''); }}
// // // //           style={{
// // // //             padding: '0.75rem 1.5rem',
// // // //             border: 'none',
// // // //             background: activeTab === 'suggestions' ? '#3498db' : 'transparent',
// // // //             color: activeTab === 'suggestions' ? 'white' : '#666',
// // // //             cursor: 'pointer',
// // // //             fontSize: '1rem'
// // // //           }}
// // // //         >
// // // //           JD Suggestions
// // // //         </button>
// // // //       </div>

// // // //       {/* ================= Resume vs JD ================= */}
// // // //       {activeTab === 'analyze' && (
// // // //         <>
// // // //           {/* Resume Upload */}
// // // //           <div className="form-group" style={{ marginBottom: '1rem' }}>
// // // //             {!resumeText ? (
// // // //               <>
// // // //                 <label>Upload Resume File</label>
// // // //                 <input type="file" accept=".txt,.pdf,.doc,.docx" onChange={handleResumeFile} />
// // // //                 <small style={{ color: '#888' }}>
// // // //                   Supported: .txt, .pdf, .doc, .docx
// // // //                 </small>
// // // //               </>
// // // //             ) : (
// // // //               <div>
// // // //                 <strong>Uploaded Resume:</strong> {resumeFileName}
// // // //                 <button
// // // //                   type="button"
// // // //                   style={{ marginLeft: 16 }}
// // // //                   onClick={() => {
// // // //                     setResumeText('');
// // // //                     setResumeFileName('');
// // // //                     setAnalysis(null);
// // // //                     setMatchPercent(null);
// // // //                   }}
// // // //                 >
// // // //                   Remove
// // // //                 </button>
// // // //               </div>
// // // //             )}
// // // //           </div>

// // // //           {/* Job Description */}
// // // //           {resumeText && (
// // // //             <form onSubmit={handleAnalyzeResume}>
// // // //               <div className="form-group">
// // // //                 <label>Job Description</label>
// // // //                 <textarea
// // // //                   value={jobDescription}
// // // //                   onChange={(e) => setJobDescription(e.target.value)}
// // // //                   placeholder="Paste job description here..."
// // // //                   style={{ minHeight: 200 }}
// // // //                 />
// // // //               </div>

// // // //               <button type="submit" disabled={loading}>
// // // //                 {loading ? 'Analyzing...' : 'Analyze Resume'}
// // // //               </button>
// // // //             </form>
// // // //           )}

// // // //           {/* ================= AI RESULT UI ================= */}
// // // //           {analysis && (
// // // //             <div style={{ marginTop: '2rem', padding: '1.5rem', background: '#f8fbff', borderRadius: 6 }}>
// // // //               <h3>Analysis Result</h3>

// // // //               {/* Match Score */}
// // // //               <div style={{ marginBottom: '1.5rem' }}>
// // // //                 <strong>Match Score</strong>
// // // //                 <div style={{ background: '#ddd', height: 20, borderRadius: 10, overflow: 'hidden', marginTop: 6 }}>
// // // //                   <div
// // // //                     style={{
// // // //                       width: `${matchPercent}%`,
// // // //                       height: '100%',
// // // //                       background:
// // // //                         matchPercent >= 75 ? '#28a745' :
// // // //                         matchPercent >= 50 ? '#ffc107' :
// // // //                         '#dc3545',
// // // //                       color: '#fff',
// // // //                       textAlign: 'center',
// // // //                       fontSize: 12,
// // // //                       lineHeight: '20px'
// // // //                     }}
// // // //                   >
// // // //                     {matchPercent}%
// // // //                   </div>
// // // //                 </div>
// // // //               </div>

// // // //               {/* Strengths */}
// // // //               <div style={{ marginBottom: '1rem' }}>
// // // //                 <strong>Strengths</strong>
// // // //                 <div style={{ marginTop: 6 }}>
// // // //                   {analysis.strengths.map((item, idx) => (
// // // //                     <span key={idx} style={{
// // // //                       display: 'inline-block',
// // // //                       background: '#d4edda',
// // // //                       color: '#155724',
// // // //                       padding: '6px 10px',
// // // //                       borderRadius: 12,
// // // //                       fontSize: 13,
// // // //                       marginRight: 6,
// // // //                       marginBottom: 6
// // // //                     }}>
// // // //                       {item}
// // // //                     </span>
// // // //                   ))}
// // // //                 </div>
// // // //               </div>

// // // //               {/* Missing Skills */}
// // // //               <div style={{ marginBottom: '1rem' }}>
// // // //                 <strong>Missing / Improvement Areas</strong>
// // // //                 <div style={{ marginTop: 6 }}>
// // // //                   {analysis.missingSkills.map((item, idx) => (
// // // //                     <span key={idx} style={{
// // // //                       display: 'inline-block',
// // // //                       background: '#fff3cd',
// // // //                       color: '#856404',
// // // //                       padding: '6px 10px',
// // // //                       borderRadius: 12,
// // // //                       fontSize: 13,
// // // //                       marginRight: 6,
// // // //                       marginBottom: 6
// // // //                     }}>
// // // //                       {item}
// // // //                     </span>
// // // //                   ))}
// // // //                 </div>
// // // //               </div>

// // // //               {/* Recommendation */}
// // // //               <div>
// // // //                 <strong>AI Recommendation</strong>
// // // //                 <div style={{
// // // //                   marginTop: 8,
// // // //                   background: '#e7f1ff',
// // // //                   padding: '10px',
// // // //                   borderRadius: 4
// // // //                 }}>
// // // //                   {analysis.recommendation}
// // // //                 </div>
// // // //               </div>
// // // //             </div>
// // // //           )}
// // // //         </>
// // // //       )}

// // // //       {/* ================= JD Suggestions ================= */}
// // // //       {activeTab === 'suggestions' && (
// // // //         <form onSubmit={handleGetSuggestions}>
// // // //           <div className="form-group">
// // // //             <label>Job Description</label>
// // // //             <textarea
// // // //               value={jobDescription}
// // // //               onChange={(e) => setJobDescription(e.target.value)}
// // // //               placeholder="Paste job description here..."
// // // //               style={{ minHeight: 200 }}
// // // //             />
// // // //           </div>

// // // //           <button type="submit" disabled={loading}>
// // // //             {loading ? 'Generating...' : 'Get JD Suggestions'}
// // // //           </button>

// // // //           {suggestions && (
// // // //             <div style={{ marginTop: '2rem', padding: '1rem', background: '#f0f7ff', borderRadius: 4 }}>
// // // //               <h3>Keyword Suggestions</h3>
// // // //               <pre style={{ whiteSpace: 'pre-wrap' }}>{suggestions}</pre>
// // // //             </div>
// // // //           )}
// // // //         </form>
// // // //       )}
// // // //     </div>
// // // //   );
// // // // }

// // // // // import React, { useState } from 'react';
// // // // // import { aiAPI } from '../api/client';
// // // // // import { toast } from 'react-toastify';

// // // // // export default function AIAssistant() {
// // // // //   const [resumeText, setResumeText] = useState('');
// // // // //   const [resumeFileName, setResumeFileName] = useState('');
// // // // //   const [jobDescription, setJobDescription] = useState('');
// // // // //   const [analysis, setAnalysis] = useState('');
// // // // //   const [matchPercent, setMatchPercent] = useState(null);
// // // // //   const [suggestions, setSuggestions] = useState('');
// // // // //   const [loading, setLoading] = useState(false);
// // // // //   const [activeTab, setActiveTab] = useState('analyze');

// // // // //   const handleAnalyzeResume = async (e) => {
// // // // //     e.preventDefault();
// // // // //     if (!resumeText) {
// // // // //       toast.error('Please upload a resume');
// // // // //       return;
// // // // //     }
// // // // //     if (!jobDescription) {
// // // // //       toast.error('Please provide a job description');
// // // // //       return;
// // // // //     }

// // // // //     setLoading(true);
// // // // //     try {
// // // // //       const response = await aiAPI.analyzeResume({
// // // // //         resumeText,
// // // // //         jobDescription
// // // // //       });
// // // // //       setAnalysis(response.data.analysis);
// // // // //       // Assume response.data.matchPercent is returned (0-100), fallback to null
// // // // //       setMatchPercent(response.data.matchPercent ?? null);
// // // // //     } catch (error) {
// // // // //       toast.error('Failed to analyze resume');
// // // // //       setMatchPercent(null);
// // // // //     } finally {
// // // // //       setLoading(false);
// // // // //     }
// // // // //   };

// // // // //   const handleGetSuggestions = async (e) => {
// // // // //     e.preventDefault();
// // // // //     if (!jobDescription) {
// // // // //       toast.error('Please provide a job description');
// // // // //       return;
// // // // //     }

// // // // //     setLoading(true);
// // // // //     try {
// // // // //       const response = await aiAPI.getJDSuggestions({
// // // // //         jobDescription
// // // // //       });
// // // // //       setSuggestions(response.data.suggestions);
// // // // //     } catch (error) {
// // // // //       toast.error('Failed to get suggestions');
// // // // //     } finally {
// // // // //       setLoading(false);
// // // // //     }
// // // // //   };

// // // // //   // Handler for file upload
// // // // //   const handleResumeFile = (e) => {
// // // // //     const file = e.target.files[0];
// // // // //     if (file) {
// // // // //       setResumeFileName(file.name);
// // // // //       const reader = new FileReader();
// // // // //       reader.onload = (event) => {
// // // // //         setResumeText(event.target.result);
// // // // //       };
// // // // //       reader.onerror = () => {
// // // // //         toast.error('Failed to read file');
// // // // //       };
// // // // //       reader.readAsText(file);
// // // // //     }
// // // // //   };

// // // // //   return (
// // // // //     <div className="card">
// // // // //       <h2>AI Assistant</h2>

// // // // //       <div style={{ marginBottom: '1.5rem', borderBottom: '2px solid #e0e0e0' }}>
// // // // //         <button 
// // // // //           onClick={() => { setActiveTab('analyze'); setAnalysis(''); }}
// // // // //           style={{ 
// // // // //             padding: '0.75rem 1.5rem',
// // // // //             border: 'none',
// // // // //             background: activeTab === 'analyze' ? '#3498db' : 'transparent',
// // // // //             color: activeTab === 'analyze' ? 'white' : '#666',
// // // // //             cursor: 'pointer',
// // // // //             fontSize: '1rem'
// // // // //           }}
// // // // //         >
// // // // //           Resume vs JD Analysis
// // // // //         </button>
// // // // //         <button 
// // // // //           onClick={() => { setActiveTab('suggestions'); setSuggestions(''); }}
// // // // //           style={{ 
// // // // //             padding: '0.75rem 1.5rem',
// // // // //             border: 'none',
// // // // //             background: activeTab === 'suggestions' ? '#3498db' : 'transparent',
// // // // //             color: activeTab === 'suggestions' ? 'white' : '#666',
// // // // //             cursor: 'pointer',
// // // // //             fontSize: '1rem'
// // // // //           }}
// // // // //         >
// // // // //           JD Suggestions
// // // // //         </button>
// // // // //       </div>

// // // // //       {/* File upload for resume - only in Resume vs JD Analysis tab */}
// // // // //       {activeTab === 'analyze' && (
// // // // //         <div className="form-group" style={{ marginBottom: '1rem' }}>
// // // // //           {!resumeText ? (
// // // // //             <>
// // // // //               <label>Upload Resume File</label>
// // // // //               <input type="file" accept=".txt,.pdf,.doc,.docx" onChange={handleResumeFile} />
// // // // //               <small style={{ color: '#888' }}>Supported: .txt, .pdf, .doc, .docx (text will be extracted if possible)</small>
// // // // //             </>
// // // // //           ) : (
// // // // //             <div style={{ marginBottom: 8 }}>
// // // // //               <strong>Uploaded Resume:</strong> {resumeFileName}
// // // // //               <button type="button" style={{ marginLeft: 16 }} onClick={() => { setResumeText(''); setResumeFileName(''); setAnalysis(''); setMatchPercent(null); }}>Remove</button>
// // // // //             </div>
// // // // //           )}
// // // // //         </div>
// // // // //       )}

// // // // //       {activeTab === 'analyze' && (
// // // // //         <form onSubmit={handleAnalyzeResume}>
// // // // //           {/* Only show JD input if resume is uploaded */}
// // // // //           {resumeText && (
// // // // //             <div className="form-group">
// // // // //               <label>Job Description</label>
// // // // //               <textarea 
// // // // //                 value={jobDescription} 
// // // // //                 onChange={(e) => setJobDescription(e.target.value)}
// // // // //                 placeholder="Paste job description here..."
// // // // //                 style={{ minHeight: '200px' }}
// // // // //               />
// // // // //             </div>
// // // // //           )}
// // // // //           <button type="submit" disabled={loading || !resumeText || !jobDescription}>
// // // // //             {loading ? 'Analyzing...' : 'Analyze Resume'}
// // // // //           </button>

// // // // //           {analysis && (
// // // // //             <div style={{ marginTop: '2rem', padding: '1rem', backgroundColor: '#f0f7ff', borderRadius: '4px', whiteSpace: 'pre-wrap', fontFamily: 'Arial' }}>
// // // // //               <h3>Analysis Result:</h3>
// // // // //               {matchPercent !== null && (
// // // // //                 <div style={{ fontWeight: 'bold', color: '#2d7a2d', marginBottom: 8 }}>
// // // // //                   Resume matches JD: {matchPercent}%
// // // // //                 </div>
// // // // //               )}
// // // // //               <p>{analysis}</p>
// // // // //             </div>
// // // // //           )}
// // // // //         </form>
// // // // //       )}

// // // // //       {activeTab === 'suggestions' && (
// // // // //         <form onSubmit={handleGetSuggestions}>
// // // // //           <div className="form-group">
// // // // //             <label>Job Description</label>
// // // // //             <textarea 
// // // // //               value={jobDescription} 
// // // // //               onChange={(e) => setJobDescription(e.target.value)}
// // // // //               placeholder="Paste job description here..."
// // // // //               style={{ minHeight: '200px' }}
// // // // //             />
// // // // //           </div>
// // // // //           <button type="submit" disabled={loading || !jobDescription}>
// // // // //             {loading ? 'Generating...' : 'Get JD Suggestions'}
// // // // //           </button>

// // // // //           {suggestions && (
// // // // //             <div style={{ marginTop: '2rem', padding: '1rem', backgroundColor: '#f0f7ff', borderRadius: '4px', whiteSpace: 'pre-wrap', fontFamily: 'Arial' }}>
// // // // //               <h3>Keyword Suggestions:</h3>
// // // // //               <p>{suggestions}</p>
// // // // //             </div>
// // // // //           )}
// // // // //         </form>
// // // // //       )}
// // // // //     </div>
// // // // //   );
// // // // // }
